import { useEffect } from 'react';

import { debateConfig } from '../../../graphdebate/config';

const isSpecifiedDataTypeMessage = (event: MessageEvent, dataTypeContaining: string | string[]) =>
  Array.isArray(dataTypeContaining)
    ? dataTypeContaining.some(type => event.data.type?.includes(type))
    : event.data.type?.includes(dataTypeContaining);

/**
 * Logs messages from other windows (sent via `postMessage`) to the console.
 * @param dataTypeContaining - keyword(s) to filter the messages to log by their `data.type` property. Can be a partial match. If not provided, all messages will be logged.
 *
 * @example
 * useWindowMessagesLogger();
 * // 📬 [WindowMessagesLogger]
 * //   MessageEvent {isTrusted: true, data: {…}, origin: …}
 *
 * @example
 * useWindowMessagesLogger('semiologic');
 * useWindowMessagesLogger(['semiologic', 'graphcomment']);
 * // 📬 [WindowMessagesLogger] New message of type  "__semiologic__RESIZE_GL__semiologic__"
 * //   MessageEvent {isTrusted: true, data: {"type": "__semiologic__RESIZE_GL__semiologic__", "height": 0}, origin: …}
 *
 */
export const useWindowMessagesLogger = (dataTypeContaining?: string | string[]) => {
  useEffect(() => {
    if (!debateConfig.ENABLE_DEBUG_POST_MESSAGES) return;
    const onMessage = (event: MessageEvent) => {
      if (dataTypeContaining) {
        if (isSpecifiedDataTypeMessage(event, dataTypeContaining)) {
          console.log(
            `📬 %c[WindowMessagesLogger] New message%c of type %c "${event.data.type}"%c`,
            'color: cyan;',
            'color: #c0c0c0;',
            'color: #ff8080;',
            'color: inherit;',
            event,
          );
        }
      } else {
        console.log('📬 %c[WindowMessagesLogger]%c', 'color: cyan;', 'color: inherit;', event);
      }
    };

    const onMessageError = (event: MessageEvent) => {
      console.warn(
        '📬 %c[WindowMessagesLogger] messageError%c',
        'color: orange;',
        'color: inherit;',
        event,
      );
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('messageerror', onMessageError);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('messageerror', onMessageError);
    };
  }, [dataTypeContaining]);

  return null;
};
